import { useEffect, useRef } from 'react';
import {
  type AllowedCountry,
  ZENDESK_DEFAULTS,
  ZENDESK_KEYS,
} from '@repo/shared-config/app/constants/zendesk';
import { getEnvironmentVariables } from '@repo/shared-config/app/helper/environment-variables';
import { isNil } from 'es-toolkit';
import { useAiAgentAbility } from '@repo/runtime-kit/abilities/use-ai-agent';
import { loadScript } from '#utils/load-script';
import { COUNTRY_TO_HELP_CENTER_MAP } from '@repo/runtime-kit/zendesk/url-composer/constants';

const ENV = getEnvironmentVariables();

/** Whether the Zendesk widget script has already loaded. Guards call sites that must not
 *  reload/re-append the widget when it's already mounted for this page (e.g. an effect that
 *  re-runs when its dependencies change). */
export const isZendeskMounted = () => !isNil(window.zE);

const _zendeskIframes = new Set<HTMLIFrameElement>();
let _iframeObserver: MutationObserver | null = null;

const _startObservingIframes = () => {
  _iframeObserver = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const node of Array.from(mutation.addedNodes)) {
        if (node instanceof HTMLIFrameElement) {
          _zendeskIframes.add(node);
        }
      }
    }
  });
  _iframeObserver.observe(document.body, { childList: true });
};

const shouldMountSandBox = ENV.deployTarget !== 'production';

export const setZendeskLocale = (locale: string) => {
  if (isZendeskMounted()) {
    window.zE?.('messenger:set', 'locale', locale);
  }
};

const _loadZendesk = async (orgCountry: AllowedCountry) => {
  let zendeskKey: string;

  if (shouldMountSandBox) {
    zendeskKey = ZENDESK_KEYS.__sandbox__;
  } else {
    zendeskKey =
      orgCountry in ZENDESK_KEYS
        ? // oxlint-disable-next-line typescript/consistent-type-assertions
          ZENDESK_KEYS[orgCountry as keyof typeof ZENDESK_KEYS] // Cast is safe here as we checked the country is in the ZENDESK_KEYS object
        : ZENDESK_KEYS[ZENDESK_DEFAULTS.UNAUTH_FALLBACK_COUNTRY];
  }

  window.zESettings = {
    messenger: {
      canUserSeeConversationList: false,
    },
  };

  _startObservingIframes();
  try {
    await loadScript(`https://static.zdassets.com/ekr/snippet.js?key=${zendeskKey}`, {
      scriptId: 'ze-snippet',
    });
  } catch {
    // we ignore the error, as Zendesk is not critical for our app to work, and we don't want to flag the error on Sentry
  }
};

export const setZendeskConversationTags = (tags: string[]): void => {
  if (isZendeskMounted()) {
    window.zE?.('messenger:on', 'open', () => {
      window.zE?.('messenger:set', 'conversationTags', tags);
    });
  }
};

export type OpenZendeskOptions = {
  /** When set, creates a new conversation and submits this message immediately. */
  initialMessage?: string;
};

/**
 * Opens the Zendesk messenger widget.
 * When `initialMessage` is provided, creates a new conversation and submits that message immediately.
 *
 * @deprecated For user-clicked support entry points, use `useOpenSupportChannel` from
 * `@repo/shared-kit/support/use-open-support-channel` instead. It routes to the unified AI chat
 * or Zendesk depending on ability and `feature--boolean-support-unified-into-ai-chat`.
 *
 * @see https://developer.zendesk.com/api-reference/widget-messaging/web/core/#create-new-conversation-ui
 */
export const openZendesk = (options: OpenZendeskOptions = {}) => {
  if (!isZendeskMounted()) {
    return;
  }

  const trimmedInitialMessage = options.initialMessage?.trim();

  if (trimmedInitialMessage) {
    // newConversation sends the message but won't expand a hidden launcher — show+open required.
    window.zE?.('messenger', 'show');
    window.zE?.('messenger:ui', 'newConversation', {
      message: {
        content: {
          type: 'text',
          text: trimmedInitialMessage,
        },
      },
    });
    window.zE?.('messenger', 'open');
    return;
  }

  window.zE?.('messenger', 'open');
};

// We delay opening the widget to give time for the redirections to finish and the page to load a bit
const _delayedOpen = () => {
  setTimeout(() => {
    openZendesk();
    window.removeEventListener('ze-loaded', _delayedOpen);
  }, 1500);
};

export const openZendeskAfterMounting = () => {
  if (isZendeskMounted()) {
    _delayedOpen();
  } else {
    window.addEventListener('ze-loaded', _delayedOpen);
  }
};

export const closeZendesk = () => {
  if (isZendeskMounted()) {
    window.zE?.('messenger', 'close');
  }
};

export const showZendesk = () => {
  if (isZendeskMounted()) {
    window.zE?.('messenger', 'show');
  }
};

export const hideZendesk = () => {
  if (isZendeskMounted()) {
    window.zE?.('messenger', 'hide');
  }
};

/**
 * Hides Zendesk for as long as the calling component is mounted when the user has no AI-agent
 * access, and restores it on unmount. A no-op when the AI chat is the active support widget — both
 * shells already keep Zendesk out of the way in that case, and toggling it here would reveal a
 * launcher stacked on top of the AI chat FAB.
 *
 * `feature--boolean-support-unified-into-ai-chat` deliberately plays no part in this: it only
 * decides whether the shell *unmounts* or *hides* Zendesk once the AI chat is active, not which
 * widget is active in the first place. Folding it into this check means that for an org with
 * AI-agent access and the flag off, Zendesk reads as active, gets hidden, and then gets restored
 * on unmount — revealing a launcher the shell had deliberately suppressed on top of the AI FAB.
 *
 * The ability is read through a ref: it can resolve after mount, and re-running this effect on
 * that flip would run its cleanup — and so `showZendesk()` — while the user is still using the flow.
 */
export function useZendeskHider(): void {
  const { canAccess: canAccessAiAgent } = useAiAgentAbility();
  const isActiveWidget = !canAccessAiAgent;

  const isActiveWidgetRef = useRef(isActiveWidget);
  useEffect(() => {
    isActiveWidgetRef.current = isActiveWidget;
  }, [isActiveWidget]);

  useEffect(() => {
    if (!isActiveWidgetRef.current) return;

    hideZendesk();
    return () => {
      showZendesk();
    };
  }, []);
}

export const loadAndAuthenticateZendesk = async ({
  orgCountry,
  userLocale,
  captureException,
}: {
  orgCountry: string;
  userLocale: string;
  captureException?: (error: unknown) => void;
}) => {
  try {
    const orgCountryToUse =
      orgCountry.toLowerCase() in COUNTRY_TO_HELP_CENTER_MAP
        ? COUNTRY_TO_HELP_CENTER_MAP[
            // oxlint-disable-next-line typescript/consistent-type-assertions
            orgCountry.toLowerCase() as keyof typeof COUNTRY_TO_HELP_CENTER_MAP
          ]
        : ZENDESK_DEFAULTS.UNAUTH_FALLBACK_COUNTRY;
    // oxlint-disable-next-line no-unused-expressions
    ZENDESK_DEFAULTS.UNAUTH_FALLBACK_COUNTRY;
    await _loadZendesk(orgCountryToUse);
    setZendeskLocale(userLocale);
    _iframeObserver?.disconnect();
    _iframeObserver = null;
    window.dispatchEvent(new Event('ze-loaded'));
  } catch (error) {
    _iframeObserver?.disconnect();
    _iframeObserver = null;
    if (captureException) {
      captureException(error);
    } else {
      // oxlint-disable-next-line no-console
      console.error('Error loading and authenticating the Zendesk widget:', error);
    }
  }
};

export const unmountZendesk = () => {
  _iframeObserver?.disconnect();
  _iframeObserver = null;

  if (isZendeskMounted()) {
    window.zE?.('messenger', 'hide');
    window.zE?.('messenger', 'logoutUser');
  }

  window.zE = undefined;
  window.zEmbed = undefined;
  window.zEACLoaded = undefined;
  window.zESettings = undefined;

  document.getElementById('ze-snippet')?.remove();

  for (const iframe of _zendeskIframes) {
    iframe.parentNode?.removeChild(iframe);
  }
  _zendeskIframes.clear();
};

export { useZendeskUnreadMessages } from './use-zendesk-unread-messages';

declare global {
  interface Window {
    zE: ((command: string, ...args: unknown[]) => void) | undefined;
    zEmbed: ((command: string, ...args: unknown[]) => void) | undefined;
    zEACLoaded: ((command: string, ...args: unknown[]) => void) | undefined;
    zESettings: Record<string, unknown> | undefined;
  }
}
